import { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { InvoiceModel, InvoiceStatus, PaymentModel, PaymentRefund } from '@frontend/api-invoices';
import { UsersApi } from '@frontend/api-users';
import { PrintDialog } from '@frontend/assets';
import { formatCentsToCurrency } from '@frontend/currency';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { downloadCSV } from '@frontend/media-helpers';
import { paymentsSentry, usePrintReceipt } from '@frontend/payments-hooks';
import {
  calculateTotalFees,
  calculateTotalNetWithSurcharge,
  isPaidOrPendingInvoice,
  calculateAppliedOrEstimatedSurcharge,
  calculateTotalPaidWithoutRefundsWithSurcharge,
  calculatePaidInvoiceTotalWithSurcharge,
  calculateUnpaidInvoiceTotalWithSurcharge,
  calculateRemainingBalanceWithoutSurcharge,
} from '@frontend/payments-invoice-calculations';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { prettify } from '@frontend/string';
import { UsersTypes } from '@frontend/user-helpers';
import { theme } from '@frontend/theme';
import {
  Button,
  Heading,
  MarkIcon,
  PrintIcon,
  RefundIcon,
  Table,
  Text,
  UnmarkIcon,
  useAlert,
  useModalControl,
} from '@frontend/design-system';
import { isCareCreditPayment } from '../../hooks/care-credit-account-utils';
import { useCanDoAction } from '../../hooks/use-can-do-action';
import { useGetCareCreditAccountNumber } from '../../hooks/use-get-care-credit-account-number';
import { useToggleInvoiceRecorded } from '../../hooks/use-mark-invoice-recorded';
import { usePrintDialogue } from '../../hooks/use-print-dialogue';
import { PaymentRow } from '../../types/invoice';
import { getPaymentMethodBrand } from '../../utils/helpers';
import { PrintHeader } from '../PrintHeader';
import { SendReceiptModal } from '../Receipts/SendReceiptModal';
import { RefundModal } from '../Refunds/RefundModal';
import { SmallTableView } from '../SmallTableView/small-table-view';
import { getSmallViewTableColumns } from '../SmallTableView/utils';
import { generateColumns, renderCardType, renderPaymentMethod } from './generate-columns';
import { getPaymentOriginLabel } from './get-payment-origin-label';
import { styles, paymentInfoStyles } from './styles';

type RequestDetailsProps = {
  invoice: InvoiceModel;
};

const getFormattedPaymentDetails = (payment: PaymentModel, careCreditAccountNumber?: string) => {
  const careCreditLastFour = careCreditAccountNumber?.replace(/\D/g, '').slice(-4);

  if (!isCareCreditPayment(payment) || !careCreditLastFour) {
    return payment.paymentDetails;
  }

  return {
    ...payment.paymentDetails,
    lastFour: careCreditLastFour,
  };
};

export const PaymentInformationTable = ({ invoice }: RequestDetailsProps) => {
  const { t } = useTranslation('payments');
  const alerts = useAlert();
  const { canRefund, canExport } = useCanDoAction(invoice.locationId);
  const print = usePrintReceipt();
  const { toggleRecorded } = useToggleInvoiceRecorded();
  const { showPrint, handlePrint } = usePrintDialogue();
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });

  const refundModalProps = useModalControl({ disableReturnFocus: true });
  const sendModalProps = useModalControl();

  const [row, setRow] = useState<PaymentRow | undefined>(undefined);

  const columns = useMemo(
    () => generateColumns(t, { hasSurchargesEnabled: invoice.surchargingEnabled }),
    [invoice.surchargingEnabled]
  );

  const careCreditAccountNumber = useGetCareCreditAccountNumber(invoice);

  const data = useFormatPaymentData(invoice, careCreditAccountNumber);

  const issueRefund = () => {
    if (!canRefund) {
      alerts.error(t(`You don't have permission to perform a refund`));
      return;
    }
    refundModalProps.triggerProps.onClick();
  };

  const isRowCareCreditPayment = (row: PaymentRow) => {
    return isCareCreditPayment({
      paymentType: row.paymentType,
      processorType: row.processorType,
    } as PaymentModel);
  };

  const transformDataForCsvExport = (data: PaymentRow[]) =>
    data.map((paymentRow) => {
      return {
        Type:
          paymentRow.status === 'PARTIALLY_REFUNDED' || paymentRow.status === 'REFUNDED' ? t('Refund') : t('Payment'),
        Date: formatDate(paymentRow.date, 'YYYY-MM-D'),
        Amount: formatCentsToCurrency(paymentRow.amount) || '0.00',
        'Collection Method': getPaymentOriginLabel({
          emptyLabel: t('N/A'),
          payment: paymentRow,
          t,
        }),
        'Payment Method': renderPaymentMethod(paymentRow.paymentType),
        'Card Type': renderCardType(paymentRow.paymentDetails?.fundingType),
        'Card Brand': `${getPaymentMethodBrand({
          paymentType: paymentRow.paymentType,
          brand: paymentRow.paymentDetails?.brand,
          bankName: paymentRow.paymentDetails?.bankName,
          processorType: paymentRow.processorType,
        })} ${paymentRow.paymentDetails.lastFour.trim()}`,
        User: paymentRow.user || t('N/A'),
        Status: prettify(paymentRow.status || '-', { split: '_', outputCase: 'Sentence case' }),
      };
    });

  const exportPayments = () => {
    try {
      if (!canExport) {
        alerts.error(t('Payment admin role required'));
        return;
      }

      if (data && data.length > 0) {
        const filename = `request_details_payment_list_${invoice.id}`;
        const transformedData = transformDataForCsvExport(data.sort((a, b) => b.date.localeCompare(a.date)));

        downloadCSV(transformedData, filename);
      }
    } catch (err) {
      paymentsSentry.error(
        'Failed to export payments',
        'PaymentInformationTable',
        `Error exporting payments for invoice ${invoice.id}: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    }
  };

  const openSendReceipt = (data: PaymentRow) => {
    setRow(data);
    sendModalProps.openModal();
  };

  const handlePrintClick = (data: PaymentRow) => {
    try {
      print(invoice, {
        receiptType: isRefund(data) ? 'refund' : 'payment',
        refundId: data.refundId,
      });
    } catch (err) {
      paymentsSentry.error(
        'Failed to print receipt',
        'PaymentInformationTable',
        `Error printing receipt for payment ${data.paymentId}: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    }
  };

  const isRefund = (row: PaymentRow | undefined) => row?.status === 'PARTIALLY_REFUNDED' || row?.status === 'REFUNDED';

  const tableActions = [
    {
      label: t('Refund'),
      Icon: RefundIcon,
      onClick: issueRefund,
      hide: (data: PaymentRow) =>
        !(
          data.status === InvoiceStatus.Paid ||
          data.status === InvoiceStatus.PartiallyPaid ||
          data.status === 'SUCCEEDED'
        ),
      trackingId: 'pay-portal-requestdetails-btn-refund',
    },
    {
      label: t('Send Receipt'),
      Icon: () => <Icon name='payments-receipt' size={16} />,
      onClick: openSendReceipt,
      hide: (data: PaymentRow) => data.status === 'FAILED',
      trackingId: 'pay-portal-requestdetails-btn-sendreceipt',
      disabled: (data: PaymentRow) => isRowCareCreditPayment(data),
      hoverLabel: (data: PaymentRow) => {
        if (isRowCareCreditPayment(data)) {
          return t('Print receipts in your provider portal.');
        }

        return undefined;
      },
    },
    {
      label: t('Print Receipt'),
      Icon: PrintIcon,
      onClick: (data: PaymentRow) => handlePrintClick(data),
      hide: (data: PaymentRow) => data.status === 'FAILED',
      trackingId: 'pay-portal-requestdetails-btn-printreceipt',
      disabled: (data: PaymentRow) => isRowCareCreditPayment(data),
      hoverLabel: (data: PaymentRow) => {
        if (isRowCareCreditPayment(data)) {
          return t('Print receipts in your provider portal.');
        }

        return undefined;
      },
    },
    {
      label: t('Mark Recorded'),
      Icon: MarkIcon,
      hide: (data: PaymentRow) =>
        !!data.recordedAt ||
        !(
          data.status === InvoiceStatus.Paid ||
          data.status === InvoiceStatus.PartiallyPaid ||
          data?.status === 'SUCCEEDED'
        ),
      onClick: (data: PaymentRow) =>
        toggleRecorded({ data: invoice, paymentId: data?.paymentId, recordedAt: data?.recordedAt }),
      trackingId: 'pay-portal-requestdetails-btn-recorded',
    },
    {
      label: t('Mark Unrecorded'),
      Icon: UnmarkIcon,
      hide: (data: PaymentRow) =>
        !data?.recordedAt ||
        !(
          data.status === InvoiceStatus.Paid ||
          data.status === InvoiceStatus.PartiallyPaid ||
          data?.status === 'SUCCEEDED'
        ),
      onClick: (data: PaymentRow) =>
        toggleRecorded({ data: invoice, paymentId: data?.paymentId, recordedAt: data?.recordedAt }),
      trackingId: 'pay-portal-requestdetails-btn-unrecorded',
    },
  ];

  return (
    <div css={styles.card}>
      <div css={paymentInfoStyles.tableHeader}>
        <Heading level={3} css={{ marginBottom: theme.spacing(2) }}>
          {t('Payment Information')}
        </Heading>
        {invoice.status !== InvoiceStatus.Pending && (
          <div>
            <Button iconName='print' onClick={handlePrint} variant='secondary' size='large' />
            <Button iconName='download' onClick={exportPayments} variant='secondary' size='large' />
          </div>
        )}
      </div>
      <PaymentBreakdown invoice={invoice} />
      {isMobile ? (
        <SmallTableView
          data={data || []}
          colConfig={getSmallViewTableColumns<PaymentRow>(columns)}
          actions={tableActions}
        />
      ) : (
        <Table
          colConfig={columns}
          data={data || []}
          rowActions={{
            actions: tableActions,
          }}
        />
      )}
      <RefundModal invoice={invoice} {...refundModalProps.modalProps} />
      <SendReceiptModal
        invoice={invoice}
        receiptType={isRefund(row) ? 'refund' : 'payment'}
        refundId={row?.refundId}
        {...sendModalProps.modalProps}
      />
      {showPrint && <PrintFriendly invoice={invoice} />}
    </div>
  );
};

const PaymentBreakdown = ({ invoice }: RequestDetailsProps) => {
  const { t } = useTranslation('payments');

  const isPaidOrPending = isPaidOrPendingInvoice(invoice);

  const totalPaid = useMemo(() => calculatePaidInvoiceTotalWithSurcharge(invoice), [invoice]);
  const totalFees = useMemo(() => calculateTotalFees(invoice), [invoice]);
  const totalNet = useMemo(() => calculateTotalNetWithSurcharge(invoice), [invoice]);
  const surchargeAmount = useMemo(() => calculateAppliedOrEstimatedSurcharge(invoice), [invoice]);
  const totalOweWithoutSurcharge = useMemo(() => calculateRemainingBalanceWithoutSurcharge(invoice), [invoice]);
  const totalOwedWithSurcharge = useMemo(() => calculateUnpaidInvoiceTotalWithSurcharge(invoice), [invoice]);

  return (
    <div css={paymentInfoStyles.paymentDetailsRow}>
      <div css={paymentInfoStyles.paymentDetails}>
        <Text color='light'>{t('Total paid')}</Text>
        <Text weight='bold'>{formatCentsToCurrency(totalPaid)}</Text>
      </div>
      {isPaidOrPending || !invoice.surchargingEnabled ? (
        <div css={paymentInfoStyles.paymentDetails}>
          <Text color='light'>{t('Total owed')}</Text>
          <Text weight='bold'>{formatCentsToCurrency(totalOweWithoutSurcharge)}</Text>
        </div>
      ) : (
        <>
          <div css={paymentInfoStyles.paymentDetails}>
            <Text color='light'>{t('Owed ACH/Debit')}</Text>
            <Text weight='bold'>{formatCentsToCurrency(totalOweWithoutSurcharge)}</Text>
          </div>
          <div css={paymentInfoStyles.paymentDetails}>
            <Text color='light'>{t('Owed Credit Card')}</Text>
            <Text weight='bold'>{formatCentsToCurrency(totalOwedWithSurcharge)}</Text>
          </div>
        </>
      )}
      {!!invoice.surchargingEnabled && (
        <div css={paymentInfoStyles.paymentDetails}>
          <Text color='light'>{isPaidOrPending ? t('Surcharge Paid') : t('Surcharge')}</Text>
          <Text weight='bold'>{formatCentsToCurrency(surchargeAmount)}</Text>
        </div>
      )}
      <div css={paymentInfoStyles.paymentDetails}>
        <Text color='light'>{t('Total fees')}</Text>
        <Text weight='bold'>{formatCentsToCurrency(totalFees)}</Text>
      </div>
      {isPaidOrPending && (
        <div css={paymentInfoStyles.paymentDetails}>
          <Text color='light'>{t('Total net')}</Text>
          <Text weight='bold'>{formatCentsToCurrency(totalNet)}</Text>
        </div>
      )}
    </div>
  );
};

const PrintFriendly = ({ invoice }: RequestDetailsProps) => {
  const { t } = useTranslation('payments');

  const columns = useMemo(
    () =>
      generateColumns(t, {
        hasSurchargesEnabled: invoice.surchargingEnabled,
      }),
    [invoice.surchargingEnabled]
  );

  const careCreditAccountNumber = useGetCareCreditAccountNumber(invoice);
  const data = useFormatPaymentData(invoice, careCreditAccountNumber);

  return (
    <PrintDialog show={true} onClose={() => {}}>
      <div
        css={css`
          margin: ${theme.spacing(2)};
          @media print {
            zoom: 75%;
          }
        `}
      >
        <PrintHeader />
        <Heading level={3} css={styles.marginBottom}>
          {t('Payment Information')}
        </Heading>
        <PaymentBreakdown invoice={invoice} />
        <Table colConfig={columns} data={data || []} />
      </div>
    </PrintDialog>
  );
};

const useFormatPaymentData = (invoice: InvoiceModel, careCreditAccountNumber?: string) => {
  const [userMap, setUserMap] = useState<UsersTypes.User[]>([]);
  const lookupUser = (userId: string) => {
    const user = userMap?.find((user: UsersTypes.User) => user.UserID === userId);
    return user ? `${user.FirstName} ${user.LastName}` : '';
  };

  const getUsers = async () => {
    try {
      const users = await UsersApi.list.users();
      setUserMap(users);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    getUsers();
  }, []);

  const getPaymentStatus = (payment: PaymentModel) => {
    if (payment.status === 'SUCCEEDED') {
      if (payment.paidAmount === invoice.billedAmount) {
        return InvoiceStatus.Paid;
      } else {
        return InvoiceStatus.PartiallyPaid;
      }
    } else if (payment.status === 'PENDING') {
      return 'PENDING';
    } else {
      return 'FAILED';
    }
  };

  const getRefundStatus = (refund: PaymentRefund) => {
    if (refund.amount === calculateTotalPaidWithoutRefundsWithSurcharge(invoice)) {
      return 'REFUNDED';
    } else {
      return 'PARTIALLY_REFUNDED';
    }
  };

  const data: PaymentRow[] = [];

  invoice.payments?.forEach((payment) => {
    if (payment) {
      const paymentDetails = getFormattedPaymentDetails(payment, careCreditAccountNumber);

      data.push({
        amount: payment.paidAmount,
        date: payment.paidAt,
        origin: payment.origin,
        paymentType: payment.paymentType,
        paymentDetails,
        paymentId: payment.paymentId,
        recordedAt: payment.recordedAt,
        status: getPaymentStatus(payment),
        user: lookupUser(payment.userId ?? ''),
        confirmationCode: payment.confirmationCode,
        appliedSurchargeAmount: invoice.appliedSurchargeAmount,
        processorType: payment.processorType,
      });
      if (payment.refunds) {
        payment.refunds.forEach(
          (refund) =>
            refund &&
            data.push({
              amount: -refund.amount,
              date: refund.createdAt,
              paymentDetails,
              paymentId: payment.paymentId,
              paymentType: payment.paymentType,
              recordedAt: payment.recordedAt,
              refundId: refund.stripeId,
              status: getRefundStatus(refund),
              user: lookupUser(refund.refundedBy),
              appliedSurchargeAmount: 0,
              processorType: payment.processorType,
            })
        );
      }
    }
  });

  data.sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf());

  return data;
};
